<template>
    <div>
        <b-container fluid>
            <h3 class="mt-3 mb-3">Client Setup</h3>

            <b-card class="mb-3" header="Step 1 — Client">
                <b-form-radio-group
                    v-model="mode"
                    :options="mode_options"
                    button-variant="outline-secondary"
                    size="sm"
                    buttons
                    class="mb-3"
                    @change="onModeChange"
                />

                <div v-if="mode === 'existing'">
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Client">
                                <b-form-select
                                    size="sm"
                                    v-model="client_id"
                                    :options="client_options"
                                    @change="onClientChange"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <div v-if="client_id">
                        <b-alert
                            v-if="existing_accounts.length === 0"
                            show
                            variant="warning"
                            class="mb-2"
                        >
                            This client has no account yet. New accounts will be
                            built from the default client user template.
                        </b-alert>
                        <b-table
                            v-else
                            small
                            outlined
                            striped
                            :items="existing_accounts"
                            :fields="existing_account_fields"
                        />
                    </div>
                </div>

                <b-row v-else>
                    <b-col md="3">
                        <b-form-group label="Client Shortname">
                            <b-form-input
                                size="sm"
                                v-model.trim="new_client.client_name"
                                placeholder="e.g. ASWI"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="5">
                        <b-form-group label="Client Name">
                            <b-form-input
                                size="sm"
                                v-model.trim="new_client.full_name"
                                placeholder="e.g. Asia Watsons International"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group label="Default Tracking Tier">
                            <b-form-radio-group
                                v-model="new_client.default_tracking_tier"
                                :options="tier_options"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-checkbox
                            switch
                            v-model="new_client.fulfil_weight_optional"
                        >
                            Fulfil Weight Optional
                        </b-form-checkbox>
                    </b-col>
                    <b-col md="3">
                        <b-form-checkbox
                            switch
                            v-model="new_client.enable_cross_platform_transfer"
                        >
                            Cross-Platform Transfer
                        </b-form-checkbox>
                    </b-col>
                </b-row>
            </b-card>

            <b-card class="mb-3" header="Step 2 — New Accounts">
                <div class="mb-2 text-muted">
                    Password is auto-generated (6 chars, editable). Leave
                    "Copy From" on Default client user template unless you want the same settings
                    and permissions as an existing account.
                </div>

                <b-table-simple small responsive>
                    <b-thead>
                        <b-tr>
                            <b-th style="width: 150px">Type</b-th>
                            <b-th>Username</b-th>
                            <b-th style="width: 200px">Password</b-th>
                            <b-th>Vendor (optional)</b-th>
                            <b-th style="width: 220px">Copy From</b-th>
                            <b-th style="width: 50px"></b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(a, idx) in new_accounts" :key="a.key">
                            <b-td>
                                <b-form-select
                                    size="sm"
                                    v-model="a.account_type"
                                    :options="account_type_options"
                                />
                            </b-td>
                            <b-td>
                                <b-form-input
                                    size="sm"
                                    v-model.trim="a.username"
                                    placeholder="username"
                                />
                            </b-td>
                            <b-td>
                                <b-input-group size="sm">
                                    <b-form-input v-model.trim="a.password" />
                                    <b-input-group-append>
                                        <b-button
                                            variant="outline-secondary"
                                            title="Regenerate password"
                                            @click="a.password = genPassword()"
                                            >&#8635;</b-button
                                        >
                                    </b-input-group-append>
                                </b-input-group>
                            </b-td>
                            <b-td>
                                <b-form-input
                                    size="sm"
                                    v-model.trim="a.vendor_name"
                                    placeholder="blank = follow source"
                                />
                            </b-td>
                            <b-td>
                                <b-form-select
                                    size="sm"
                                    v-model="a.source_user_id"
                                    :options="source_options"
                                    :disabled="mode !== 'existing'"
                                />
                            </b-td>
                            <b-td>
                                <b-button
                                    size="sm"
                                    variant="outline-danger"
                                    :disabled="new_accounts.length <= 1"
                                    @click="new_accounts.splice(idx, 1)"
                                    >&times;</b-button
                                >
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-button size="sm" variant="outline-secondary" @click="addRow()"
                    >+ Add Account</b-button
                >
            </b-card>

            <div class="text-right mb-4">
                <b-button size="sm" variant="outline-secondary" class="mr-2" @click="reset()"
                    >Reset</b-button
                >
                <b-button
                    size="sm"
                    variant="info"
                    :disabled="!canCreate || saving"
                    @click="onSave()"
                    >{{ saving ? "Saving..." : "Save" }}</b-button
                >
            </div>
        </b-container>

        <b-modal
            v-model="result_modal"
            title="Setup Complete"
            ok-only
            size="lg"
            @hidden="afterResult()"
        >
            <p v-if="result">
                <strong>{{ result.client_label }}</strong>
                <span v-if="result.is_new_client"> (new client created)</span>
                <span v-if="result.client_permissions_copied">
                    — {{ result.client_permissions_copied }} client permission
                    rows copied from template</span
                >
            </p>
            <p class="text-danger mb-2">
                Save the passwords now. They cannot be viewed again.
            </p>
            <b-table-simple small v-if="result">
                <b-thead>
                    <b-tr>
                        <b-th>Type</b-th>
                        <b-th>Username</b-th>
                        <b-th>Password</b-th>
                        <b-th>Permissions copied</b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="a in result.accounts" :key="a.id">
                        <b-td>{{ a.account_type }}</b-td>
                        <b-td>{{ a.username }}</b-td>
                        <b-td
                            ><code>{{ a.password }}</code></b-td
                        >
                        <b-td>{{ a.permissions_copied }}</b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-modal>
    </div>
</template>

<script>
import global_function from "../mixins/global_function";

let row_key = 0;

export default {
    name: "CmsClientSetup",
    mixins: [global_function],
    inject: ["API_HOST"],
    data() {
        return {
            mode: "existing",
            mode_options: [
                { text: "Existing client", value: "existing" },
                { text: "New client", value: "new" }
            ],
            tier_options: [
                { text: "Tier 1", value: "1" },
                { text: "Tier 2", value: "2" },
                { text: "Tier 3", value: "3" }
            ],
            account_type_options: [
                { text: "client user", value: "client_user" },
                { text: "client admin", value: "client_admin" }
            ],
            existing_account_fields: [
                { key: "username", label: "Username" },
                { key: "account_type", label: "Type" },
                { key: "vendor_name", label: "Vendor" },
                { key: "last_login_at", label: "Last Login" }
            ],
            clients: [],
            client_id: null,
            existing_accounts: [],
            new_client: this.blankClient(),
            new_accounts: [],
            saving: false,
            result: null,
            result_modal: false
        };
    },
    async created() {
        if (!this.isAllowed) {
            this.$router.replace("/");
            return;
        }
        this.addRow();
        await this.loadClients();
    },
    computed: {
        isAllowed() {
            const u = this.$store.state.user_obj;
            return !!u && u.client_id == 3 && u.account_type == "admin";
        },
        client_options() {
            return [
                { text: "Select client", value: null },
                ...this.clients.map(c => ({
                    text: `${c.client_name} — ${c.full_name} (${c.account_count} accounts)`,
                    value: c.id
                }))
            ];
        },
        source_options() {
            return [
                { text: "Default client user template", value: null },
                ...this.existing_accounts.map(a => ({
                    text: `${a.username} (${a.account_type})`,
                    value: a.id
                }))
            ];
        },
        canCreate() {
            return this.mode === "new" || !!this.client_id;
        }
    },
    methods: {
        blankClient() {
            return {
                client_name: "",
                full_name: "",
                default_tracking_tier: null,
                fulfil_weight_optional: false,
                enable_cross_platform_transfer: false
            };
        },
        genPassword(len = 6) {
            const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
            const arr = new Uint32Array(len);
            window.crypto.getRandomValues(arr);
            return Array.from(arr, n => chars[n % chars.length]).join("");
        },
        addRow() {
            this.new_accounts.push({
                key: ++row_key,
                account_type: "client_user",
                username: "",
                password: this.genPassword(),
                vendor_name: "",
                source_user_id: null
            });
        },
        showError(message) {
            this.$eventHub.$emit("show_modal_message", {
                message_content: message,
                variant: "danger"
            });
        },
        async post(url, data) {
            const response = await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: url,
                data: { user_id: this.$store.state.user_obj.user_id, ...data }
            });
            return response.data;
        },
        async loadClients() {
            const res = await this.post("cms_client_setup_get.php", {
                client_id: this.client_id
            });
            if (res.status !== "success") {
                this.showError(res.message);
                return;
            }
            this.clients = res.clients;
            this.existing_accounts = res.accounts;
        },
        async onClientChange() {
            this.existing_accounts = [];
            this.new_accounts.forEach(a => (a.source_user_id = null));
            if (this.client_id) {
                await this.loadClients();
            }
        },
        onModeChange() {
            this.new_accounts.forEach(a => (a.source_user_id = null));
        },
        reset() {
            this.client_id = null;
            this.existing_accounts = [];
            this.new_client = this.blankClient();
            this.new_accounts = [];
            this.addRow();
        },
        validate() {
            if (this.mode === "new") {
                const c = this.new_client;
                if (!c.client_name || !c.full_name || !c.default_tracking_tier) {
                    return "Client shortname, name and default tier are required.";
                }
            } else if (!this.client_id) {
                return "Please select a client.";
            }
            if (this.mode === "existing" && this.new_accounts.length === 0) {
                return "Add at least one account.";
            }
            for (const [i, a] of this.new_accounts.entries()) {
                if (!a.username || !a.password) {
                    return `Account #${i + 1}: username and password are required.`;
                }
            }
            return null;
        },
        async onSave() {
            const error = this.validate();
            if (error) {
                this.showError(error);
                return;
            }

            const accounts = this.new_accounts.map(a => ({
                account_type: a.account_type,
                username: a.username,
                password: a.password,
                vendor_name: a.vendor_name,
                source_user_id: a.source_user_id
            }));
            const data = { accounts };
            if (this.mode === "new") {
                data.client = this.new_client;
            } else {
                data.client_id = this.client_id;
            }

            this.saving = true;
            try {
                const res = await this.post("cms_client_setup_save.php", data);
                if (res.status !== "success") {
                    const names = res.usernames ? `: ${res.usernames.join(", ")}` : "";
                    this.showError(res.message + names);
                    return;
                }
                const client =
                    this.mode === "new"
                        ? this.new_client.client_name
                        : (this.clients.find(c => c.id == this.client_id) || {}).client_name;
                this.result = {
                    client_id: res.client_id,
                    client_label: client,
                    is_new_client: res.is_new_client,
                    client_permissions_copied: res.client_permissions_copied,
                    accounts: res.accounts_created.map((a, i) => ({
                        ...a,
                        password: this.new_accounts[i].password
                    }))
                };
                this.result_modal = true;
            } catch (e) {
                this.showError("Network or server error.");
            } finally {
                this.saving = false;
            }
        },
        async afterResult() {
            // reselect the client so its account list reflects the new accounts
            const client_id = this.result.client_id;
            this.reset();
            this.mode = "existing";
            this.client_id = client_id;
            await this.loadClients();
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/components/CmsClientSetup.vue